import { computed } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { DisplayListViewModel } from '../DisplayFilters/DisplayListViewModel';
import { ActiveDisplay } from './ActiveDisplay';
import { cellIdToString, IDisplayCellMeta, IDisplayCellProps } from '../Displays/DisplayCells';
import { displayAggregateCells } from '../Displays/DisplayCells_Aggregate';
import { IMultiSelectOption, bind, componentAutoReaction, inject, autoinject } from 'Mri.Web.Common';
import { displayTelemetryCells } from './DisplayCells_Telemetry';
import { displayConfigCells } from './DisplayCells_Config';
import { AppStore } from '../Core/AppStore';

export interface IDisplayRowProps {
    onFavoritesClick?(display: ActiveDisplay): void;
    renderBefore?(display: ActiveDisplay): React.ReactNode;
    renderAfter?(display: ActiveDisplay): React.ReactNode;
    readonly display: ActiveDisplay;
    readonly vm: DisplayListViewModel;
    readonly whitelist?: IMultiSelectOption<string | Mri.WebApi.MeasurementId>[];
    readonly history: any;
    readonly rowNumber?: number;
    readonly aggregateView?: boolean;
}

@observer
export class DisplayRow extends React.Component<IDisplayRowProps> {
    public render() {
        const { display, vm, history, rowNumber, onFavoritesClick, aggregateView } = this.props;
        return (
            <tr
                className={this.rowClassName}
                onMouseEnter={() => vm.onDisplayHover(display)}
                onMouseLeave={() => vm.onDisplayHover()}
            >
                {this.props.renderBefore && <td>{this.props.renderBefore(display)}</td>}

                {!this.whitelistedCells &&
                    this.displayCells.map(c => (
                        <TableCell
                            key={c.id}
                            cell={c}
                            rowNumber={rowNumber}
                            vm={vm}
                            display={display}
                            history={history}
                            onFavoritesClick={onFavoritesClick}
                        />
                    ))}

                {this.whitelistedCells &&
                    this.whitelistedCells.map(c => (
                        <TableCell
                            key={c.id}
                            cell={c}
                            rowNumber={rowNumber}
                            vm={vm}
                            display={display}
                            history={history}
                            onFavoritesClick={onFavoritesClick}
                        />
                    ))}

                {this.props.renderAfter && <td>{this.props.renderAfter(display)}</td>}
               
            </tr>
        );
    }

    @computed
    private get displayCells() {
        const { vm } = this.props;
        const { stickyColumns } = vm;

        const selectedColumns = vm.columns;
        const selectedConfigColumns = vm.configColumns;

        const cells: IDisplayCellMeta[] = [];
        const configCells: IDisplayCellMeta[] = [];

        for (const column of selectedColumns) {
            const cell = displayTelemetryCells.find(c => c.id == column.option);
            if (cell) {
                const index = displayTelemetryCells.indexOf(cell);
                cells.splice(index, 0, cell);
            }
        }

        for (const configColumn of selectedConfigColumns) {
            const configCell = displayConfigCells.find(c => c.id == configColumn.option);
            if (configCell) {
                const index = displayTelemetryCells.indexOf(configCell);
                configCells.splice(index, 0, configCell);
            }
        }

        const joinedCells = cells.concat(configCells);

        joinedCells.sort((a, b) => {
            return stickyColumns.indexOf(cellIdToString(b.id)) - stickyColumns.indexOf(cellIdToString(a.id));
        });

        return this.props.aggregateView ? displayAggregateCells : joinedCells;
    }

    @computed
    private get whitelistedCells() {
        if (this.props.whitelist) {
            const cells: IDisplayCellMeta[] = [];

            for (const column of this.props.whitelist) {
                const cell = displayTelemetryCells.find(c => c.id == column.option);
                if (cell) cells.push(cell);
            }

            return cells;
        }
    }

    @computed
    private get rowClassName() {
        const { display, vm } = this.props;
        let classname = '';
        if (display.muted) classname = classname + ' text-muted';
        if (vm.isHovered(display)) classname = classname + ' display-row-highlight';

        return classname;
    }
}

// TableCell is a Mobx optimization to wrab the renderer in an @observer
interface ITableCellProps extends IDisplayCellProps {
    readonly cell: IDisplayCellMeta;
    readonly vm: DisplayListViewModel;
}

@observer
class TableCell extends React.Component<ITableCellProps> {
    public render() {
        const { render } = this.props.cell;

        return render(this.props);
    }
}
